<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { gsap } from 'gsap';
import { resources } from '../data/resources.js';

const root = ref(null);
const activeStep = ref(0);
const resultResources = [
  resources.find(resource => resource.type === 'website'),
  resources.find(resource => resource.type === 'app'),
  resources.find(resource => resource.type === 'center')
].filter(Boolean);
const sourceResource = computed(() => resultResources[0]);
let context;

const stepMeta = [
  { number:'01',title:'ابحث أو اختر قسمًا',description:'ابدأ بكلمة بحث واضحة أو اختر القسم الأقرب إلى احتياجك.',icon:'bi-search' },
  { number:'02',title:'استكشف الموارد',description:'قارن بين الموارد الموثوقة واختر الأنسب لطريقة استخدامك.',icon:'bi-grid' },
  { number:'03',title:'انتقل إلى المصدر',description:'راجع بيانات المورد ثم انتقل مباشرة إلى مصدره الرسمي.',icon:'bi-box-arrow-up-left' }
];

async function selectStep(index) {
  if (activeStep.value === index) return;
  activeStep.value = index;
  await nextTick();
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const card = root.value?.querySelectorAll('.journey-step-card')[index];
  if (!card) return;
  gsap.fromTo(card.querySelector('.journey-step-body'),{ autoAlpha:.55,y:5 },{ autoAlpha:1,y:0,duration:.38,ease:'power3.out',clearProps:'transform,opacity,visibility' });
  gsap.fromTo(card.querySelector('.journey-step-icon'),{ scale:.92 },{ scale:1,duration:.34,ease:'back.out(1.8)',clearProps:'transform' });
}

function runEntrance() {
  if (!root.value || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  context = gsap.context(() => {
    gsap.timeline({ scrollTrigger:{ trigger:root.value,start:'top 76%',once:true } })
      .from('.journey-steps-eyebrow',{ y:8,autoAlpha:0,duration:.38,ease:'power3.out' })
      .from('.journey-steps-title>span',{ yPercent:105,autoAlpha:0,duration:.84,ease:'power4.out' },'-=.12')
      .from('.journey-steps-header>p',{ y:10,autoAlpha:0,duration:.4,ease:'power3.out' },'-=.42')
      .from('.journey-steps-header>i',{ scaleX:0,transformOrigin:'center',duration:.45,ease:'power3.out' },'-=.18')
      .from('.journey-step-card',{ y:22,scale:.985,autoAlpha:0,stagger:.09,duration:.68,ease:'power3.out' },'-=.12');
  },root.value);
}

onMounted(runEntrance);
onBeforeUnmount(() => context?.revert());
</script>

<template>
  <section id="journey" ref="root" class="section journey journey-steps-section" aria-labelledby="journey-steps-heading">
    <div class="journey-steps-shell">
      <header class="journey-steps-header">
        <span class="journey-steps-eyebrow">01 — 03</span>
        <h2 id="journey-steps-heading" class="journey-steps-title"><span>من البحث إلى المورد المناسب</span></h2>
        <p>خطوات بسيطة تساعدك للوصول إلى محتوى موثوق يخدمك.</p>
        <i aria-hidden="true"></i>
      </header>

      <div class="journey-steps-grid">
        <article v-for="(step,index) in stepMeta" :key="step.number" class="journey-step-card" :class="{active:activeStep===index}">
          <header class="journey-step-card-header">
            <em>{{ step.number }}</em>
            <button type="button" class="journey-step-icon" :aria-label="`تحديد خطوة ${step.number}: ${step.title}`" :aria-pressed="activeStep===index" @click="selectStep(index)"><i class="bi" :class="step.icon"></i></button>
          </header>
          <div class="journey-step-heading">
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </div>

          <div v-if="index===0" class="journey-step-body journey-search-card-ui">
            <label class="journey-search-field"><span class="visually-hidden">عبارة البحث</span><input value="السنة النبوية" type="search"><i class="bi bi-search"></i></label>
            <div class="journey-search-controls"><button type="button">جميع الأقسام <i class="bi bi-chevron-down"></i></button><RouterLink :to="{name:'search',query:{q:'السنة النبوية',page:'1'}}">بحث <i class="bi bi-arrow-left"></i></RouterLink></div>
            <small>اقتراحات سريعة</small>
            <div class="journey-search-chips"><RouterLink :to="{name:'search',query:{q:'صحة الحديث',page:'1'}}">صحة الحديث</RouterLink><RouterLink :to="{name:'search',query:{q:'شروح الأحاديث',page:'1'}}">شروح الأحاديث</RouterLink><RouterLink :to="{name:'search',query:{q:'تطبيقات السنة',page:'1'}}">تطبيقات السنة</RouterLink></div>
          </div>

          <div v-else-if="index===1" class="journey-step-body journey-real-results">
            <RouterLink v-for="resource in resultResources" :key="resource.id" :to="resource.path" class="journey-real-result">
              <img :src="resource.image" :alt="resource.title"><span><b>{{ resource.title }}</b><small>{{ resource.shortDescription || resource.description }}</small></span><i class="bi bi-arrow-left"></i>
            </RouterLink>
            <RouterLink class="journey-more-link" :to="{name:'search',query:{page:'1'}}">عرض المزيد <i class="bi bi-arrow-left"></i></RouterLink>
          </div>

          <div v-else-if="sourceResource" class="journey-step-body journey-source-preview">
            <figure><div class="journey-source-browser"><i></i><i></i><i></i></div><img :src="sourceResource.image" :alt="sourceResource.title"></figure>
            <span>{{ sourceResource.typeLabel }}</span>
            <h4>{{ sourceResource.title }}</h4>
            <p>{{ sourceResource.shortDescription || sourceResource.description }}</p>
            <a v-if="sourceResource.primaryAction" :href="sourceResource.primaryAction.url" target="_blank" rel="noopener noreferrer">زيارة الموقع <i class="bi bi-box-arrow-up-left"></i></a>
          </div>
        </article>
      </div>

    </div>
  </section>
</template>
